import { Component, type ErrorInfo, type ReactNode } from 'react';
import {
  ExclamationTriangleIcon,
  HomeIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

/**
 * Composant ErrorBoundary pour capturer et gérer les erreurs React
 * avec une interface utilisateur propre et des options de récupération
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Met à jour l'état pour afficher l'interface d'erreur
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log l'erreur pour le debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // TODO: Envoyer l'erreur à un service de monitoring (Sentry, LogRocket, etc.)
    if (import.meta.env.PROD) {
      this.logErrorToService(error, errorInfo);
    }
  }

  private logErrorToService = (error: Error, errorInfo: ErrorInfo): void => {
    // TODO: Intégrer un service de monitoring d'erreurs
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    // Exemple d'envoi vers un service de monitoring
    if (import.meta.env.DEV) {
      console.log('Error data that would be sent to monitoring service:', errorData);
    }
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleGoHome = (): void => {
    window.location.href = '/';
  };

  private handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });
  };

  private toggleDetails = (): void => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails,
    }));
  };

  private formatErrorDetails = (): string => {
    const { error, errorInfo } = this.state;
    if (!error || !errorInfo) return '';

    return `Erreur: ${error.message}

Stack trace:
${error.stack}

Component stack:
${errorInfo.componentStack}

Timestamp: ${new Date().toISOString()}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}`;
  };

  private copyErrorToClipboard = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(this.formatErrorDetails());
      // TODO: Afficher une notification de succès
      alert("Détails de l'erreur copiés dans le presse-papiers");
    } catch (err) {
      console.error('Impossible de copier dans le presse-papiers:', err);
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Interface d'erreur personnalisée
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow-xl rounded sm:px-10">
              {/* Icône d'erreur */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
                </div>
              </div>

              {/* Titre et message */}
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-3">
                  Oups ! Une erreur s'est produite
                </h1>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Nous sommes désolés, mais quelque chose s'est mal passé. Notre équipe a été
                  automatiquement notifiée de ce problème.
                </p>
              </div>

              {/* Boutons d'action */}
              <div className="space-y-3 mb-6">
                <button
                  onClick={this.handleRetry}
                  className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                >
                  <ArrowPathIcon className="w-4 h-4 mr-2" />
                  Réessayer
                </button>

                <button
                  onClick={this.handleGoHome}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                >
                  <HomeIcon className="w-4 h-4 mr-2" />
                  Retour à l'accueil
                </button>

                <button
                  onClick={this.handleReload}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                >
                  Recharger la page
                </button>
              </div>

              {/* Section détails d'erreur (développement) */}
              {import.meta.env.DEV && this.state.error && (
                <div className="border-t border-gray-200 pt-6">
                  <button
                    onClick={this.toggleDetails}
                    className="w-full flex items-center justify-between text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200"
                  >
                    <span>Détails techniques</span>
                    {this.state.showDetails ? (
                      <ChevronUpIcon className="w-4 h-4" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4" />
                    )}
                  </button>

                  {this.state.showDetails && (
                    <div className="mt-4 space-y-3">
                      <div className="bg-red-50 border border-red-200 rounded p-3">
                        <h4 className="text-sm font-medium text-red-800 mb-2">Message d'erreur:</h4>
                        <p className="text-xs text-red-700 font-mono break-words">
                          {this.state.error.message}
                        </p>
                      </div>

                      {this.state.error.stack && (
                        <div className="bg-gray-50 border border-gray-200 rounded p-3">
                          <h4 className="text-sm font-medium text-gray-800 mb-2">Stack trace:</h4>
                          <pre className="text-xs text-gray-600 font-mono overflow-x-auto whitespace-pre-wrap">
                            {this.state.error.stack}
                          </pre>
                        </div>
                      )}

                      <button
                        onClick={this.copyErrorToClipboard}
                        className="w-full px-3 py-2 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors duration-200"
                      >
                        Copier les détails de l'erreur
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Informations d'assistance */}
              <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                <p className="text-xs text-gray-500">
                  Si le problème persiste, contactez notre support technique
                </p>
                <p className="text-xs text-gray-400 mt-1">ID d'erreur: {Date.now().toString(36)}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
